import { computed } from 'vue'
import { useHead } from '#imports'
import { useI18n } from '~/composables/useI18n'

const SITE_BASE_URL = 'https://www.weryai.com'

const DEFAULT_LOCALE = 'en'

const BREADCRUMB_HOME: Record<string, string> = {
    en: 'Home', zh: '首页', ja: 'ホーム', ko: '홈', es: 'Inicio', fr: 'Accueil',
    de: 'Startseite', ru: 'Главная', pt: 'Início', it: 'Home', ar: 'الرئيسية',
    tw: '首頁', pl: 'Strona główna', th: 'หน้าแรก', da: 'Hjem', no: 'Hjem',
    nl: 'Home', id: 'Beranda', tr: 'Ana Sayfa',
}

const BREADCRUMB_AI_VIDEO: Record<string, string> = {
    en: 'AI Video', zh: 'AI 视频', ja: 'AI 動画', ko: 'AI 비디오', es: 'Video IA',
    fr: 'Vidéo IA', de: 'KI-Video', ru: 'ИИ Видео', pt: 'Vídeo IA', it: 'Video IA',
    ar: 'فيديو AI', tw: 'AI 影片', pl: 'Wideo AI', th: 'วิดีโอ AI', da: 'AI Video',
    no: 'AI Video', nl: 'AI-video', id: 'Video AI', tr: 'AI Video',
}

/**
 * Inject a 3-level BreadcrumbList JSON-LD: Home > AI Video > {pageName}
 *
 * Fully reactive: locale changes (e.g. client-side language switch) update
 * both breadcrumb names and URL locale prefixes automatically.
 *
 * @param pageNameKey - i18n key for the page name (e.g. 'seo.imageToVideo.page.title1')
 * @param pathWithoutLocale - path without locale prefix (e.g. '/image-to-video')
 */
export function useVideoBreadcrumb(pageNameKey: string, pathWithoutLocale: string) {
    const { t, locale } = useI18n()

    const currentLocale = computed(() => locale.value || DEFAULT_LOCALE)

    const localePrefix = computed(() =>
        currentLocale.value === DEFAULT_LOCALE ? '' : `/${currentLocale.value}`
    )

    const buildUrl = (path: string) =>
        `${SITE_BASE_URL}${localePrefix.value}${path}`

    useHead({
        script: [
            {
                key: 'breadcrumb-schema',
                type: 'application/ld+json',
                innerHTML: computed(() =>
                    JSON.stringify({
                        '@context': 'https://schema.org',
                        '@type': 'BreadcrumbList',
                        itemListElement: [
                            {
                                '@type': 'ListItem',
                                position: 1,
                                name: BREADCRUMB_HOME[currentLocale.value] || 'Home',
                                item: buildUrl('/'),
                            },
                            {
                                '@type': 'ListItem',
                                position: 2,
                                name: BREADCRUMB_AI_VIDEO[currentLocale.value] || 'AI Video',
                                item: buildUrl('/ai-video'),
                            },
                            {
                                '@type': 'ListItem',
                                position: 3,
                                name: t(pageNameKey),
                                item: buildUrl(pathWithoutLocale),
                            },
                        ],
                    })
                ),
            },
        ],
    })
}
